/**
 * Soft-Send Undo-Mechanismus (Phase 6.4, 2026-05-08).
 *
 * Backend queued jeden Reply / Forward / Calendar-Notify und sendet erst nach
 * `delay_s` Sekunden tatsaechlich per SMTP. In der Zwischenzeit kann der User
 * den Send via DELETE /api/v1/sends/{id} stoppen.
 *
 * Frontend-Konzept: Send-Forms rufen `notifyPendingSend()` mit dem Response
 * auf. Eine globale `<UndoSendToast>`-Komponente lauscht auf das CustomEvent
 * und rendert einen Toast mit Countdown + Stoppen-Button. So muessen die
 * einzelnen Forms keine eigene Toast-Logik bauen.
 *
 * Fehlerpfad (06.08.2026): Scheiterte der SMTP-Versand NACH dem Undo-Fenster,
 * bekam der Nutzer davon nichts mit — der Toast war weg, `failed` kannte das
 * Frontend nicht, und die Mail war lautlos verloren (Vorfall pending_sends
 * 6/7/8, Auth-Fehler bei M365 bzw. Gmail-App-Passwort). Jetzt zwei Wege:
 *  1. sofort: der Toast pollt nach Ablauf des Countdowns den Endstatus,
 *  2. spaeter: `<FailedSendAlert>` zeigt beim naechsten Laden alle
 *     unquittierten Fehler der letzten 24h mit der error_message aus der DB.
 */

export interface PendingSendInfo {
  pending_send_id?: number | null
  scheduled_at?: string | null
  delay_s?: number | null
}

export interface UndoSendDetail {
  pendingId: number
  scheduledAt: string  // ISO
  delayS: number
  label: string        // z.B. "Antwort an foo@bar.de"
  onCancelled?: () => void  // wird aufgerufen wenn User Stoppen geklickt hat
}

export const UNDO_SEND_EVENT = "portal:undo-send"

/**
 * Wenn der Backend-Response eine pending_send_id enthaelt → Undo-Toast triggern.
 * Forms koennen den Response einfach durchreichen ohne Toast-Code zu duplizieren.
 */
export function notifyPendingSend(
  resp: PendingSendInfo | null | undefined,
  label: string,
  onCancelled?: () => void,
): boolean {
  if (!resp || !resp.pending_send_id || !resp.scheduled_at) {
    return false
  }
  const detail: UndoSendDetail = {
    pendingId: resp.pending_send_id,
    scheduledAt: resp.scheduled_at,
    delayS: resp.delay_s ?? 30,
    label,
    onCancelled,
  }
  window.dispatchEvent(new CustomEvent(UNDO_SEND_EVENT, { detail }))
  return true
}

export async function cancelPendingSend(pendingId: number): Promise<boolean> {
  try {
    const r = await fetch(`/api/v1/sends/${pendingId}`, {
      method: "DELETE",
      credentials: "include",
    })
    if (r.ok) return true
    // 409 = nicht mehr abbrechbar (zu spaet), 404 = nicht gefunden
    return false
  } catch {
    return false
  }
}

// ─── Fehlgeschlagene Sends ───────────────────────────────────────────────────

export type SendStatus =
  | "pending" | "sending" | "sent" | "cancelled" | "failed"

export interface FailedSend {
  id: number
  tenant_slug: string
  email_id: number | null
  kind: string
  attempts: number
  error_message: string | null
  /** Empfaenger + Betreff aus dem payload — damit klar ist WELCHE Mail liegenblieb. */
  to: string
  subject: string
  created_at: string | null
  sent_at: string | null
  acknowledged_at: string | null
}

/** Endstatus eines Sends. `null` wenn (noch) nicht abrufbar. */
export async function fetchSendStatus(
  pendingId: number,
): Promise<{ status: SendStatus; error_message: string | null } | null> {
  try {
    const r = await fetch(`/api/v1/sends/${pendingId}`, { credentials: "include" })
    if (!r.ok) return null
    const d = await r.json()
    if (!d?.item?.status) return null
    return {
      status: d.item.status as SendStatus,
      error_message: d.item.error_message ?? null,
    }
  } catch {
    return null
  }
}

/** Unquittierte Fehl-Sends der letzten `hours` Stunden, ueber alle Tenants. */
export async function fetchFailedSends(hours = 24): Promise<FailedSend[]> {
  try {
    const r = await fetch(`/api/v1/sends/failed?hours=${hours}`, {
      credentials: "include",
    })
    if (!r.ok) return []
    const d = await r.json()
    return Array.isArray(d?.items) ? (d.items as FailedSend[]) : []
  } catch {
    return []
  }
}

/** Quittiert einen Fehler — danach alarmieren Banner und Health-Check nicht mehr. */
export async function acknowledgeFailedSend(pendingId: number): Promise<boolean> {
  try {
    const r = await fetch(`/api/v1/sends/${pendingId}/ack`, {
      method: "POST",
      credentials: "include",
    })
    return r.ok
  } catch {
    return false
  }
}

export async function acknowledgeAllFailedSends(hours = 24): Promise<boolean> {
  try {
    const r = await fetch(`/api/v1/sends/failed/ack?hours=${hours}`, {
      method: "POST",
      credentials: "include",
    })
    return r.ok
  } catch {
    return false
  }
}

/** Menschenlesbares Label fuer die `kind`-Spalte. */
export function sendKindLabel(kind: string): string {
  switch (kind) {
    case "reply": return "Antwort"
    case "forward": return "Weiterleitung"
    case "compose": return "Neue E-Mail"
    case "calendar_notify": return "Termin-Info"
    default: return kind
  }
}

/**
 * Uebersetzt die technische SMTP-Fehlermeldung in einen Satz, mit dem der
 * Nutzer etwas anfangen kann. Die Originalmeldung bleibt zusaetzlich sichtbar.
 */
export function explainSendError(raw: string | null | undefined): string | null {
  const m = (raw || "").toLowerCase().trim()
  if (!m) return null
  // „{}" = smtplib ohne einen einzigen Empfaenger (Berggoetz 29.10.2026).
  if (m === "{}" || m.includes("kein gültiger empfänger")) {
    return "Im An-Feld stand keine Adresse, die der Mailserver annimmt. " +
      "Bitte nur die reine Adresse eintragen (z. B. name@firma.de) und neu senden."
  }
  if (m.includes("535") || m.includes("534") || m.includes("auth") ||
      m.includes("invalid_grant") || m.includes("credential") ||
      m.includes("basic authentication is disabled")) {
    return "Der Mail-Zugang hat die Anmeldung abgelehnt — Passwort bzw. App-Passwort " +
      "des Absender-Postfachs pruefen (bei Microsoft 365 ggf. Basic Auth deaktiviert)."
  }
  if (m.includes("550") || m.includes("553") || m.includes("5.1.1") ||
      m.includes("recipient") || m.includes("user unknown")) {
    return "Der Empfaenger wurde vom Server abgelehnt — Adresse pruefen."
  }
  // Vor der generischen Timeout-Regel — sonst verschluckt die das hier.
  if (m.includes("sending-timeout")) {
    return "Der Versand haengt seit dem letzten Neustart — Zustand unklar, " +
      "bitte im Gesendet-Ordner nachsehen bevor du erneut sendest."
  }
  if (m.includes("timed out") || m.includes("timeout")) {
    return "Zeitueberschreitung zum Mailserver. Nicht automatisch wiederholt, " +
      "weil die Mail sonst doppelt rausgehen koennte — bitte manuell pruefen."
  }
  return null
}
